import type { FeedsClient } from '@stream-io/feeds-client'

import { isFetchResultSuccessful } from '../../entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../entities/NotificationCenter/NotificationCenter'
import { endEventLoggerApiCall, startEventLoggerApiCall } from '../../entities/NotificationCenter/NotificationCenterService'
import { ValkTalkPostsQuery } from '../../entities/ValkTalkPost/ValkTalkPost'
import { mergeSavedValkTalkPosts } from '../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { deleteValkTalkPostApiCall, getValkTalkPostsApiCall } from '../../entities/ValkTalkPost/ValkTalkPostService'
import { ReducerGetter, TypedDispatch } from '../store'
import {
    removePost,
    setError,
    setIsDeleting,
    setIsLoading,
    setPosts,
} from './valkTalkPostsReducer'

export const fetchValkTalkPosts = (
    client: FeedsClient,
    query: ValkTalkPostsQuery,
) => async (dispatch: TypedDispatch, getState: ReducerGetter): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await getValkTalkPostsApiCall(client, query)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setPosts({ posts: [], hasMorePosts: false }))
            dispatch(setError(response.error))

            return
        }

        const { savedPosts, deletedPostIds } = getState().valkTalkPostsReducer
        const posts = mergeSavedValkTalkPosts(response.data.posts, savedPosts, deletedPostIds, query)

        dispatch(setPosts({ posts, hasMorePosts: response.data.hasMorePosts }))
    } catch (error) {
        console.error('[fetchValkTalkPosts]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const deleteValkTalkPost = (postId: string) => async (dispatch: TypedDispatch): Promise<boolean> => {
    dispatch(setIsDeleting(true))
    dispatch(setError(''))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.FEED_MESSAGE,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(setIsDeleting(false))

        return false
    }

    try {
        const response = await deleteValkTalkPostApiCall(postId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return false
        }

        dispatch(removePost(postId))

        return true
    } catch (error) {
        console.error('[deleteValkTalkPost]', error)

        return false
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsDeleting(false))
    }
}
